import { createApi, FetchBaseQueryError } from '@reduxjs/toolkit/query/react';
import { RecordResponse } from '../models/ServiceTypes';
import { waServicesQuery } from './baseQueries';
import { WARootState } from './store';

interface NotificationData {
  NotificationId: number;
  FileName: string;
  VaultFileId: string;
}

export interface Notification {
  Notification: string;
  CreateDate: string;
  FromUserName: string;
  NotificationTypeId: number;
  IsRead: boolean;
  Data: NotificationData[];
  isSearchBoxClearEnabled: boolean;
}

export interface NotificationTypeRecord {
  NotificationTypeId: number;
  Name: string;
  Code: string;
  Role: string;
  Description: string;
  IsEmailEnabled: boolean;
  CanChangeEnabled: boolean;
  Frequency: number;
  CanChangeFrequency: boolean;
  FrequencyDisplay: string;
}

export type NotificationTypesResponse = RecordResponse<NotificationTypeRecord>;

// --- Start: Types for Notification Settings ---
export interface NotificationSettingRecord {
  CanChange: boolean;
  Email: string;
  Active: boolean;
  CreateDate: string; // Consider using Date type if transformation is done
}

export type NotificationSettingsResponse =
  RecordResponse<NotificationSettingRecord>;
// --- End: Types for Notification Settings ---

// --- Start: Types for Grouped Alerts ---
export interface WealthAlert {
  WealthId: number;
  WealthName: string;
  AlertValue: number | null;
}

export interface AccountAlert {
  WealthAccountId: number;
  WealthId: number;
  WealthName: string;
  WealthAccountName: string;
  AlertValue: number | null;
}

export interface GroupedAlert {
  Code:
    | 'NWGreater'
    | 'NWLess'
    | 'BEGreater'
    | 'BDGreater'
    | 'BRTLess'
    | 'BTGreater'
    | 'ABLess'
    | 'ABGreater';
  CanSetForAllAccounts: boolean;
  CanSetForSpecificAccounts: boolean;
  Enabled: boolean | null;
  Name: string;
  AlertValue: number | null;
  Wealths: WealthAlert[];
  Accounts: AccountAlert[];
}

export type GroupedAlertsResponse = RecordResponse<GroupedAlert>;
// --- End: Types for Grouped Alerts ---

export const notificationsApi = createApi({
  reducerPath: 'notificationsApi',
  baseQuery: waServicesQuery,
  tagTypes: [
    'Notifications',
    'NotificationTypes',
    'NotificationSettings',
    'GroupedAlerts',
  ],
  endpoints: (builder) => ({
    getNotifications: builder.query<RecordResponse<Notification>, void>({
      queryFn: async (_arg, { getState }, _extra, baseQuery) => {
        const state = getState() as WARootState;
        const currentRole = state.auth.currentRole;

        const result = await baseQuery({
          url: `/api/${currentRole}/notifications`,
          method: 'GET',
        });

        if (result.error) {
          return { error: result.error };
        }

        return { data: result.data as RecordResponse<Notification> };
      },
      providesTags: ['Notifications'],
    }),
    markAllNotificationsRead: builder.mutation<void, void>({
      async queryFn(_arg, { getState }, _extra, baseQuery) {
        const state = getState() as WARootState;
        const currentRole = state.auth.currentRole;
        const result = await baseQuery({
          url: `/api/${currentRole}/notifications`,
          method: 'PUT',
        });
        if (result.error) {
          return { error: result.error };
        }
        return { data: undefined };
      },
      invalidatesTags: ['Notifications'],
    }),
    getNotificationTypes: builder.query<NotificationTypesResponse, void>({
      query: () => ({
        url: '/api/user/notificationTypes',
        method: 'GET',
        params: {
          limit: 100,
          'sort[0].Property': 'Name',
          'sort[0].Direction': 'Asc',
          'sort[0].Order': 0,
        },
      }),
      providesTags: ['NotificationTypes'],
    }),
    updateNotificationTypeSetting: builder.mutation<
      NotificationTypeRecord,
      NotificationTypeRecord
    >({
      query: (settingToUpdate) => ({
        url: `/api/user/notificationTypes/${settingToUpdate.NotificationTypeId}`,
        method: 'PUT',
        body: settingToUpdate,
      }),
      invalidatesTags: ['NotificationTypes'],
    }),
    getNotificationSettings: builder.query<NotificationSettingsResponse, void>({
      query: () => ({
        url: '/api/user/notificationSettings',
        method: 'GET',
      }),
      providesTags: ['NotificationSettings'],
    }),
    updateNotificationSettings: builder.mutation<
      boolean,
      NotificationSettingRecord
    >({
      query: (settingToUpdate) => ({
        url: '/api/user/notificationSettings',
        method: 'POST',
        body: settingToUpdate,
      }),
      invalidatesTags: ['NotificationSettings'],
    }),
    getGroupedAlerts: builder.query<GroupedAlertsResponse, void>({
      queryFn: async (_arg, { getState }, _extra, baseQuery) => {
        const state = getState() as WARootState;
        const currentRole = state.auth.currentRole?.toLowerCase();

        if (!currentRole) {
          return {
            error: {
              status: 'CUSTOM_ERROR',
              error: 'Missing current role for grouped alerts request.',
            } satisfies FetchBaseQueryError,
          };
        }

        const result = await baseQuery({
          url: `/api/${currentRole}/groupedAlerts`,
          method: 'GET',
          params: {
            _: Date.now(),
          },
        });

        if (result.error) {
          return { error: result.error };
        }

        return { data: result.data as GroupedAlertsResponse };
      },
      providesTags: ['GroupedAlerts'],
    }),
    updateGroupedAlert: builder.mutation<boolean, GroupedAlert>({
      queryFn: async (alertToUpdate, { getState }, _extra, baseQuery) => {
        const state = getState() as WARootState;
        const currentRole = state.auth.currentRole?.toLowerCase();

        if (!currentRole) {
          return {
            error: {
              status: 'CUSTOM_ERROR',
              error: 'Missing current role for grouped alerts update.',
            } satisfies FetchBaseQueryError,
          };
        }

        const result = await baseQuery({
          url: `/api/${currentRole}/groupedAlerts/${alertToUpdate.Code}`,
          method: 'PUT',
          body: alertToUpdate,
        });

        if (result.error) {
          return { error: result.error };
        }

        return { data: result.data as boolean };
      },
      onQueryStarted: async (alertToUpdate, { dispatch, queryFulfilled }) => {
        const patchResult = dispatch(
          notificationsApi.util.updateQueryData(
            'getGroupedAlerts',
            undefined, // No args for getGroupedAlerts
            (draft) => {
              const index = draft.Records.findIndex(
                (alert) => alert.Code === alertToUpdate.Code,
              );
              if (index !== -1) {
                // Merge the updated fields into the existing alert
                draft.Records[index] = {
                  ...draft.Records[index],
                  ...alertToUpdate,
                };
              }
            },
          ),
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),
  }),
});

export const {
  useGetNotificationsQuery,
  useMarkAllNotificationsReadMutation,
  useGetNotificationTypesQuery,
  useUpdateNotificationTypeSettingMutation,
  useGetNotificationSettingsQuery,
  useUpdateNotificationSettingsMutation,
  useGetGroupedAlertsQuery,
  useUpdateGroupedAlertMutation,
} = notificationsApi;
